import React, { createContext, useContext, useState, useEffect } from 'react';

interface UsageData {
  date: string;
  scriptsGenerated: number;
  charactersCreated: number;
  storyboardsCreated: number;
  projectsCreated: number;
  exportsCount: number;
}

interface AnalyticsData {
  totalUsage: {
    scriptsGenerated: number;
    charactersCreated: number;
    storyboardsCreated: number;
    projectsCreated: number;
    exportsCount: number;
  };
  monthlyUsage: UsageData[];
  popularGenres: { genre: string; count: number }[];
  usageTrends: { date: string; usage: number }[];
  featureUsage: {
    scriptGeneration: number;
    characterManagement: number;
    storyboardDesign: number;
    resourceCenter: number;
    exportFunctionality: number;
  };
}

interface AnalyticsContextType {
  analyticsData: AnalyticsData | null;
  isLoading: boolean;
  refreshAnalytics: () => Promise<void>;
  getUsageByDateRange: (startDate: string, endDate: string) => Promise<UsageData[]>;
  getMostUsedFeatures: () => Promise<{ feature: string; usage: number }[]>;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadAnalyticsData();
  }, []);

  const loadAnalyticsData = async () => {
    try {
      setIsLoading(true);

      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 1500));

      // 生成模拟数据
      const mockData: AnalyticsData = {
        totalUsage: {
          scriptsGenerated: 47,
          charactersCreated: 23,
          storyboardsCreated: 15,
          projectsCreated: 12,
          exportsCount: 8
        },
        monthlyUsage: generateMonthlyUsage(),
        popularGenres: [
          { genre: '爱情', count: 18 },
          { genre: '喜剧', count: 12 },
          { genre: '悬疑', count: 8 },
          { genre: '动作', count: 6 },
          { genre: '科幻', count: 3 }
        ],
        usageTrends: generateUsageTrends(),
        featureUsage: {
          scriptGeneration: 45,
          characterManagement: 32,
          storyboardDesign: 28,
          resourceCenter: 19,
          exportFunctionality: 15
        }
      };

      setAnalyticsData(mockData);
    } catch (error) {
      console.error('Failed to load analytics data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateMonthlyUsage = (): UsageData[] => {
    const usageData: UsageData[] = [];
    const now = new Date();

    for (let i = 29; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);

      usageData.push({
        date: date.toISOString().split('T')[0],
        scriptsGenerated: Math.floor(Math.random() * 5),
        charactersCreated: Math.floor(Math.random() * 3),
        storyboardsCreated: Math.floor(Math.random() * 2),
        projectsCreated: Math.floor(Math.random() * 2),
        exportsCount: Math.floor(Math.random() * 2)
      });
    }

    return usageData;
  };

  const generateUsageTrends = () => {
    const trends = [];
    const now = new Date();

    for (let i = 6; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i * 7);

      trends.push({
        date: date.toISOString().split('T')[0],
        usage: Math.floor(Math.random() * 50) + 20
      });
    }

    return trends;
  };

  const refreshAnalytics = async () => {
    await loadAnalyticsData();
  };

  const getUsageByDateRange = async (startDate: string, endDate: string): Promise<UsageData[]> => {
    try {
      setIsLoading(true);

      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 500));

      if (!analyticsData) return [];

      return analyticsData.monthlyUsage.filter(usage =>
        usage.date >= startDate && usage.date <= endDate
      );
    } catch (error) {
      console.error('Failed to get usage by date range:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getMostUsedFeatures = async (): Promise<{ feature: string; usage: number }[]> => {
    try {
      setIsLoading(true);

      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 500));

      if (!analyticsData) return [];

      return [
        { feature: '剧本生成', usage: analyticsData.featureUsage.scriptGeneration },
        { feature: '角色管理', usage: analyticsData.featureUsage.characterManagement },
        { feature: '分镜设计', usage: analyticsData.featureUsage.storyboardDesign },
        { feature: '资源中心', usage: analyticsData.featureUsage.resourceCenter },
        { feature: '导出功能', usage: analyticsData.featureUsage.exportFunctionality }
      ].sort((a, b) => b.usage - a.usage);
    } catch (error) {
      console.error('Failed to get most used features:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const value: AnalyticsContextType = {
    analyticsData,
    isLoading,
    refreshAnalytics,
    getUsageByDateRange,
    getMostUsedFeatures
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};